import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../../../common/ConstantsLagoon';
import { StyledDiv } from '../../../../../../../common/ConstantsStyles';
import { ModalWithTracking } from '../../../../../../../common/ModalWithTracking/ModalWithTracking';
import { PortalIdentifier } from '../../../../../../../common/Portal';
import { PSXIcon } from '../../../../../../../common/PSXIcon';
import { PortalContext } from '../../../../../../../context/PortalContext';
import { useCoverageType } from '../../../../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../../../../hooks/useFeatureFlag';
import { useSmartChoice } from '../../../../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../../../../hooks/useStore/state';
import { useStore } from '../../../../../../../hooks/useStore/useStore';
import { useSuppressNiceMembers } from '../../../../../../../hooks/useSuppressNiceMembers';
import { CompareProvider } from '../../../../../../../models/Provider';
import { returnSuppressFlag } from '../../../../../../../utils/featureSuppress';
import { shouldUseGehaV2Styling } from '../../../../../../../utils/gehaThemeUtils';
import { getCurrentMember } from '../../../../../../../utils/user.utils';
import { getDefaultCompareAttributes } from '../../../../ViewAll/utils/getDefaultCompareAttributes';
import { compareModalButtonStyle } from '../../utility/compareDrawer.styled';
import {
  CHECKBOX_ITEM_NAMES,
  CompareCheckboxInfo,
} from '../../utility/compareDrawerConstants';

export type CustomAttributesBlock = {
  correlationId: string;
  providerId: string;
  providerType: string;
};
interface DataForAnalytics {
  linkName: string;
  linkLocation: string;
  modalName: string;
  linkType?: string;
  customAttributesBlock?: CustomAttributesBlock;
}
type Props = {
  setSelectedProviders?: (a: CompareProvider[]) => void;
  setSelectedCheckbox?: (a: { checked: {} }) => void;
  setOpenCompare?: (a: boolean) => void;
  setSelectedItems?: React.Dispatch<React.SetStateAction<CompareProvider[]>>;
  setItems?: React.Dispatch<React.SetStateAction<CompareCheckboxInfo[]>>;
  handleClickCb?: () => void;
  closeButtonText?: string;
  isViewProvider?: boolean;
  locationForAnalytics?: string;
  dataForAnalytics?: DataForAnalytics;
  modal?: any;
  modalName?: string;
};

export const ConfirmationModal = ({
  setSelectedProviders,
  setSelectedCheckbox,
  setSelectedItems,
  setOpenCompare,
  setItems,
  closeButtonText,
  handleClickCb,
  isViewProvider,
  locationForAnalytics,
  dataForAnalytics,
  modal,
  modalName,
}: Props) => {
  const { t } = useTranslation();
  const coverageType = useCoverageType();
  const suppressPremiumCare: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PREMIUM_CARE
  );
  const { suppressForNiceMembers: suppressPremiumCareForNiceMembers } =
    useSuppressNiceMembers();
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const suppressPatientReviews: boolean = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.PATIENT_REVIEWS
  );

  const currentMember = getCurrentMember();

  const showMergedCompareAndShare = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_COMPARE_SMART_CHOICE,
  ]);

  const showTier2Badge = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER,
  ]);

  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const isIFPTieredPlan = currentMember?.isIFPTieredPlan && showTier2Badge;

  const isCns = currentMember?.isCnSMember;
  const handleAdobeLinkTrack = (buttonName: string) => {
    adobeLinkTrackEvent({
      name: buttonName,
      location: locationForAnalytics ?? `modal:close confirmation`,
      type: 'internal',
    });
  };
  const handleAdobeLinkTrackEventSmartChoice = (buttonName: string) => {
    adobeLinkTrackEvent({
      name: buttonName,
      location: 'are you sure you want to exit',
      type: 'Internal',
    });
  };

  const handleClick = () => {
    handleAdobeLinkTrack('done');
    if (setOpenCompare) {
      setOpenCompare(false);
    }
    if (setSelectedProviders) {
      setSelectedProviders([]);
    }
    if (setSelectedCheckbox) {
      setSelectedCheckbox({ checked: {} });
    }
    const defaultCompareAttribute = getDefaultCompareAttributes(
      coverageType,
      showMergedCompareAndShare && isSmartChoice
    );
    const newItems = defaultCompareAttribute
      .filter((item) => {
        if (item.name === CHECKBOX_ITEM_NAMES.PREMIUM_CARE_PROVIDER) {
          return !(suppressPremiumCare || suppressPremiumCareForNiceMembers);
        }
        if (item.name === CHECKBOX_ITEM_NAMES.REVIEWS) {
          return !suppressPatientReviews;
        }
        if (item.name === CHECKBOX_ITEM_NAMES.TIER_PROVIDER) {
          return !(isIFPTieredPlan || isCns);
        }
        if (item.name === CHECKBOX_ITEM_NAMES.TIER_2_PROVIDER) {
          return isIFPTieredPlan;
        }
        return true;
      })
      .map((item) => ({
        ...item,
      }));

    if (setItems) {
      setItems(newItems);
    }
  };

  // begin: a11y keyboard navigation: move focus to last  selected provider index
  const setUIstate = useStore(StoreKeys.SET_UI_STATE);
  const uiState = useStore(StoreKeys.UI_STATE);
  const { compareProvidersFlow } = useStore(StoreKeys.UI_STATE);
  const { indexOflastProviderSelected } = compareProvidersFlow;

  const handleFocusLastIndex = () => {
    setUIstate({
      ...uiState,
      compareProvidersFlow: {
        indexOflastProviderSelected,
        shouldFocusLastIndex: true,
      },
    });
  };
  // end

  return (
    <ModalWithTracking
      analyticsInfo={{
        linkName: dataForAnalytics?.linkName,
        linkLocation: dataForAnalytics?.linkLocation,
        modalName: dataForAnalytics?.modalName,
        customAttributesBlock: dataForAnalytics?.customAttributesBlock,
      }}
      closeOnClickOutside={false}
      css={{
        '.abyss-modal-header-container': { paddingTop: '0px' },
        '.abyss-modal-base-content-container': {
          width: 'auto',
          height: 'auto !important',
          borderRadius: '8px',
        },
        '.abyss-modal-content-title': {
          marginTop: '36px',
          height: '84px',
        },
        '.abyss-modal-close-button': {
          marginRight: '16px',
          marginTop: '16px',
          width: 'auto',
          svg: {
            width: '18px',
            height: '20px',
            color: '#1F2937',
            fill: '#1F2937',
          },
        },
        '.abyss-modal-overlay': {
          background: 'rgba(0, 0, 0, 0.7)',
        },
      }}
      data-auto-testid="confirmation-modal-container"
      data-testid="confirmation-modal-container"
      model={modalName}
      onClose={() => {
        if (showMergedCompareAndShare) {
          handleAdobeLinkTrackEventSmartChoice('close');
        } else {
          handleAdobeLinkTrack('close');
        }
        modal.close();
      }}
      size="320px"
      title={
        <div style={{ marginTop: '23px' }}>
          {isGehaTheme ? (
            <IconSymbol
              color="$core.color.brand.100"
              icon="error"
              size={84}
              variant="outlined"
            />
          ) : (
            <PSXIcon icon="real_time_clock" size={84} />
          )}
        </div>
      }
      trackingType={Constants.ADOBE_TRACKING.MODAL_WITH_LINK_TRACKING}
    >
      <StyledDiv
        css={{
          textAlign: 'center',
          padding: '0px 16px 25px 16px',
          lineHeight: '18px',
        }}
      >
        <Text fontSize="sm" fontWeight="bold">
          {isViewProvider
            ? t(
                'COMPARE_DRAWER_CONFIRMATION_MODAL.MODAL_SECTION_PART_ONE_VIEW_PROVIDER'
              )
            : t('COMPARE_DRAWER_CONFIRMATION_MODAL.MODAL_SECTION_PART_ONE')}
        </Text>
        <br />
        <Text fontSize="sm">
          {isViewProvider
            ? t(
                'COMPARE_DRAWER_CONFIRMATION_MODAL.MODAL_SECTION_PART_TWO_VIEW_PROVIDER'
              )
            : t('COMPARE_DRAWER_CONFIRMATION_MODAL.MODAL_SECTION_PART_TWO')}
        </Text>
      </StyledDiv>
      <div>
        <Layout.Group alignLayout="center" space={8}>
          <Button
            css={compareModalButtonStyle}
            data-auto-testid="confirmation-modal-close-drawer"
            data-testid="confirmation-modal-close-drawer"
            onClick={() => {
              setSelectedItems?.([]);
              setSelectedCheckbox?.({ checked: {} });
              if (showMergedCompareAndShare) {
                handleAdobeLinkTrackEventSmartChoice('Yes, I’m done');
              }
              modal.close();
              if (handleClickCb) {
                handleClickCb();
              } else {
                handleClick();
              }
            }}
            size="md"
          >
            {closeButtonText ||
              t('COMPARE_DRAWER_CONFIRMATION_MODAL.CLOSE_BUTTON')}
          </Button>
          <Button
            css={compareModalButtonStyle}
            data-auto-testid="confirmation-modal-keep-comparing"
            data-testid="confirmation-modal-keep-comparing"
            onClick={() => {
              if (showMergedCompareAndShare) {
                handleAdobeLinkTrackEventSmartChoice('keep working');
              } else {
                handleAdobeLinkTrack('keep working');
              }
              handleFocusLastIndex();
              modal.close();
            }}
            size="md"
            variant="outline"
          >
            {t('COMPARE_DRAWER_CONFIRMATION_MODAL.CONTINUE_WORKING_BUTTON')}
          </Button>
        </Layout.Group>
      </div>
    </ModalWithTracking>
  );
};

ConfirmationModal.defaultProps = {
  isViewProvider: false,
};
